// Where a notification click should navigate, for the current user's ROLE.
//
// SINGLE SOURCE OF TRUTH shared by the header bell (NotificationBell) and the
// "See all" page (NotificationsPage). A notification's stored `link` is set once
// at creation and is often role-specific (a client-portal route, an /admin route)
// while the SAME notification is shown to several roles. Following the raw link
// blindly sent users to routes their role can't open — which used to log them
// out, and now (with the route safety net) drops them on their dashboard.
//
// The fix: route by PERMISSION. Follow the stored deep-link only when the user's
// role can actually open that route; otherwise fall back to the case (or the
// role's own console) so the click always lands somewhere useful — precisely,
// for every role.

// Roles allowed on each linkable route. MUST mirror the allowedRoles in
// src/App.js for these routes. (If it drifts, a notification degrades to the
// case/dashboard instead of its precise deep-link — never a crash or logout.)
// Longest matching prefix wins, so more-specific routes override their parents.
const ROUTE_ACCESS = [
  { prefix: '/sales/leads',      roles: ['sales_consultant', 'sales_manager', 'super_admin', 'admin', 'process_manager', 'marketing_manager'] },
  { prefix: '/admin/cases',      roles: ['super_admin', 'admin', 'process_manager', 'legal_counsel'] },
  { prefix: '/admin/clients',    roles: ['super_admin', 'admin', 'process_manager', 'sales_manager', 'sales_consultant'] },
  { prefix: '/admin/complaints', roles: ['super_admin', 'admin', 'process_manager'] },
  { prefix: '/admin/hr/job-applications', roles: ['super_admin', 'admin', 'process_manager'] },
  { prefix: '/admin/discount',   roles: ['super_admin', 'admin', 'sales_manager', 'marketing_manager', 'process_manager', 'accountant', 'legal_counsel'] },
  { prefix: '/admin/accounting', roles: ['super_admin', 'admin', 'accountant'] },
  { prefix: '/admin/leads/deletion-report',     roles: ['super_admin'] },
  { prefix: '/admin/leads/reassignment-report', roles: ['super_admin', 'admin', 'sales_manager'] },
  { prefix: '/officer/cases',      roles: ['case_officer', 'super_admin', 'admin', 'process_manager', 'legal_counsel'] },
  { prefix: '/officer/complaints', roles: ['case_officer'] },
  { prefix: '/legal/escalations',  roles: ['legal_counsel', 'super_admin', 'admin', 'process_manager', 'case_officer', 'sales_consultant', 'client'] },
  { prefix: '/accountant/accounting', roles: ['accountant'] },
  { prefix: '/tasks',            roles: ['super_admin', 'admin', 'process_manager', 'case_officer', 'sales_manager', 'sales_consultant', 'accountant'] },
  { prefix: '/client/documents', roles: ['client'] },
  { prefix: '/client',           roles: ['client'] },
];

// Can `role` open `path`? Matches the longest route prefix (query string ignored).
// Unknown / dead routes (e.g. the legacy /cases/:id/documents, /admin/leads,
// /admin/documents, /admin/agreements) match nothing → false, so we never follow
// a link that would dead-end; the case fallback handles those instead.
export function canOpen(path, role) {
  if (!path || !role) return false;
  const clean = path.split('?')[0];
  let best = null;
  for (const r of ROUTE_ACCESS) {
    if (clean === r.prefix || clean.startsWith(r.prefix + '/')) {
      if (!best || r.prefix.length > best.prefix.length) best = r;
    }
  }
  return best ? best.roles.includes(role) : false;
}

// Returns the path to navigate to, or null to inline-expand the message.
export function notificationTarget(n, role) {
  if (!n) return null;
  const link = n.link;
  const t = (n.type || '').toLowerCase();

  // 1) Console-action types → the role's OWN console (keeps accountants in
  //    /accountant/*, officers in /officer/*), preserving the entity id.
  if (t.startsWith('payment') || t === 'lead_pending_verification') {
    const base = role === 'accountant' ? '/accountant/accounting' : '/admin/accounting';
    if (canOpen(base, role)) {
      const m = /[?&]verify=([^&]+)/.exec(link || '');
      return `${base}?tab=pending${m ? `&verify=${m[1]}` : ''}`;
    }
  }
  if (t.startsWith('complaint')) {
    if (role === 'case_officer') return '/officer/complaints';
    if (canOpen('/admin/complaints', role)) return '/admin/complaints';
  }

  // 2) Follow the stored deep-link when the role can open that exact route —
  //    the precise destination set at creation (lead, case, escalation, client…).
  if (link && canOpen(link, role)) return link;

  // 3) Cross-role or dead link → open the case in a section the role can access.
  if (n.case_id) {
    if (canOpen('/admin/cases', role)) return `/admin/cases/${n.case_id}`;
    if (canOpen('/officer/cases', role)) return `/officer/cases/${n.case_id}`;
  }

  // 4) Nothing safe/known to navigate to → inline-expand the message.
  return null;
}


// Autopilot action chips (briefing + advisory answers) → permission-aware
// deep link, reusing the same route-access table. Returns null when the
// role can't open anywhere useful — the chip renders as plain text.
export function actionTarget(action, role) {
  if (!action?.entity_id) return null;
  if (action.entity_type === 'lead') {
    const p = `/sales/leads/${action.entity_id}`;
    if (canOpen(p, role)) return p;
  }
  if (action.entity_type === 'case') {
    if (canOpen('/admin/cases', role)) return `/admin/cases/${action.entity_id}`;
    if (canOpen('/officer/cases', role)) return `/officer/cases/${action.entity_id}`;
  }
  return null;
}
